import React, { useEffect, useMemo, useState } from 'react';

import { Button } from '../../../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../ui/dialog';
import { Input } from '../../../ui/input';
import { Label } from '../../../ui/label';
import { TimeInput } from '../../../ui/time-input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../ui/select';
import { Textarea } from '../../../ui/textarea';
import type {
  CreateScheduledJobPayload,
  ScheduledJob,
  ScheduleCadence,
} from '../../../../types/scheduledJobs';
import { CADENCE_LABELS } from '../../../../types/scheduledJobs';

interface CreateScheduledJobDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (payload: CreateScheduledJobPayload) => Promise<void> | void;
  /** Pass a job to switch the dialog into edit mode. */
  job?: ScheduledJob | null;
  isSubmitting?: boolean;
}

type FormErrors = Partial<Record<'name' | 'prompt' | 'daily_time_utc', string>>;

const DEFAULT_STATE = {
  name: '',
  prompt: '',
  cadence: 'every_10m' as ScheduleCadence,
  daily_time_utc: '09:00',
};

// Accept either HH:MM (what the <input type="time"> produces) or HH:MM:SS
// (what the backend returns — Pydantic serialises Python `time` with seconds).
const TIME_REGEX = /^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/;

/** Normalise a backend HH:MM:SS into the HH:MM shape `<input type="time">` expects. */
const toInputTime = (value: string | null | undefined): string => {
  if (!value) return '09:00';
  const match = value.match(/^([01]\d|2[0-3]):[0-5]\d/);
  return match ? match[0] : value;
};

const validate = (values: typeof DEFAULT_STATE): FormErrors => {
  const errors: FormErrors = {};
  if (!values.name.trim()) {
    errors.name = 'Name is required';
  }
  if (!values.prompt.trim()) {
    errors.prompt = 'Prompt is required';
  }
  if (values.cadence === 'daily') {
    if (!values.daily_time_utc.trim()) {
      errors.daily_time_utc = 'Daily time is required';
    } else if (!TIME_REGEX.test(values.daily_time_utc.trim())) {
      errors.daily_time_utc = 'Use 24-hour HH:MM (UTC)';
    }
  }
  return errors;
};

/**
 * Create / edit dialog for a scheduled agent job.
 *
 * Also used in edit mode — pass `job` and the form hydrates from that record.
 */
const CreateScheduledJobDialog: React.FC<CreateScheduledJobDialogProps> = ({
  open,
  onOpenChange,
  onSubmit,
  job = null,
  isSubmitting = false,
}) => {
  const isEdit = Boolean(job);
  const [values, setValues] = useState(DEFAULT_STATE);
  const [errors, setErrors] = useState<FormErrors>({});
  const [attempted, setAttempted] = useState(false);

  // Reset form whenever the dialog is (re-)opened or the edited job changes.
  useEffect(() => {
    if (!open) return;
    if (job) {
      setValues({
        name: job.name,
        prompt: job.prompt,
        cadence: job.cadence,
        daily_time_utc: toInputTime(job.daily_time_utc),
      });
    } else {
      setValues(DEFAULT_STATE);
    }
    setErrors({});
    setAttempted(false);
  }, [open, job]);

  const cadenceOptions = useMemo(
    () =>
      (Object.keys(CADENCE_LABELS) as ScheduleCadence[]).map((key) => ({
        value: key,
        label: CADENCE_LABELS[key],
      })),
    [],
  );

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setAttempted(true);
    const nextErrors = validate(values);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const payload: CreateScheduledJobPayload = {
      name: values.name.trim(),
      prompt: values.prompt.trim(),
      cadence: values.cadence,
    };
    if (values.cadence === 'daily') {
      payload.daily_time_utc = values.daily_time_utc.trim();
    }
    await onSubmit(payload);
  };

  const showError = (field: keyof FormErrors) =>
    attempted && errors[field] ? errors[field] : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit scheduled job' : 'Create scheduled job'}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Update the agent schedule. Changes take effect on the next tick.'
              : 'Define a recurring agent run. The agent picks skills from the prompt intent at runtime.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5" noValidate>
          <div className="space-y-2">
            <Label htmlFor="scheduled-job-name">Name</Label>
            <Input
              id="scheduled-job-name"
              value={values.name}
              onChange={(event) =>
                setValues((prev) => ({ ...prev, name: event.target.value }))
              }
              placeholder="e.g. Hourly Intercom sync"
              aria-invalid={Boolean(showError('name')) || undefined}
              aria-describedby={showError('name') ? 'scheduled-job-name-error' : undefined}
            />
            {showError('name') && (
              <p
                id="scheduled-job-name-error"
                className="text-xs text-destructive"
              >
                {showError('name')}
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-[1fr_auto]">
            <div className="space-y-2">
              <Label htmlFor="scheduled-job-cadence">Cadence</Label>
              <Select
                value={values.cadence}
                onValueChange={(next) =>
                  setValues((prev) => ({
                    ...prev,
                    cadence: next as ScheduleCadence,
                  }))
                }
              >
                <SelectTrigger
                  id="scheduled-job-cadence"
                  className="w-full"
                  data-testid="scheduled-job-cadence-trigger"
                >
                  <SelectValue placeholder="Choose a cadence" />
                </SelectTrigger>
                <SelectContent>
                  {cadenceOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {values.cadence === 'daily' && (
              <TimeInput
                id="scheduled-job-daily-time"
                label="Daily time"
                value={values.daily_time_utc}
                onChange={(next) =>
                  setValues((prev) => ({ ...prev, daily_time_utc: next }))
                }
                error={showError('daily_time_utc')}
              />
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="scheduled-job-prompt">Prompt</Label>
            <Textarea
              id="scheduled-job-prompt"
              value={values.prompt}
              onChange={(event) =>
                setValues((prev) => ({ ...prev, prompt: event.target.value }))
              }
              placeholder="Describe in natural language what the agent should do. e.g. 'Use the intercom-reporting skill to import new conversations since the last run and flag any with Consumer Duty risk.'"
              rows={6}
              className="min-h-[160px] font-sans text-sm leading-relaxed"
              aria-invalid={Boolean(showError('prompt')) || undefined}
              aria-describedby={
                showError('prompt') ? 'scheduled-job-prompt-error' : undefined
              }
            />
            {showError('prompt') && (
              <p
                id="scheduled-job-prompt-error"
                className="text-xs text-destructive"
              >
                {showError('prompt')}
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              The agent has the full <code>go_intelligence/skills/</code> library available.
              Be specific about which skills to invoke.
            </p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting
                ? isEdit
                  ? 'Saving…'
                  : 'Creating…'
                : isEdit
                  ? 'Save changes'
                  : 'Create job'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateScheduledJobDialog;
